import { useState, useMemo, useCallback } from 'react';
import { Box, Paper, Typography, Stack, CircularProgress, Alert, Link as MuiLink } from '@mui/material';
import InsightsEmptyState from '@/components/common/InsightsEmptyState';
import { useStaggeredAnimation, useFetchCallInsights } from '@/hooks';
import { useFetchCampaignQuestions } from '@/components/campaigns/CampaignQuestionsEditor';
import { Link as RouterLink } from 'react-router';
import CallInsightItem from './CallInsightItem';
import CallInsightFilters from './CallInsightFilters';
import CallInsightsFetcher from './CallInsightsFetcher';
import CallInsightsLayout from './CallInsightsLayout';
import CallInsightsHeader from './CallInsightsHeader';

import { QuestionType } from '@/components/campaigns/CampaignQuestionsEditor/types';

interface CallInsightsProps {
    campaignId: number | null;
    callId: number | null;
}

/**
 * Call insights component that shows the call insights generated from the questions
 * defined in the project.
 *
 * @param {CallInsightsProps} props
 * @returns {JSX.Element}
 */
const CallInsights: React.FC<CallInsightsProps> = ({ campaignId = null, callId = null }) => {
    const [selectedTypes, setSelectedTypes] = useState<QuestionType[]>([]);

    const {
        data: questions = [],
        storage,
        isLoading: questionsLoading,
        isError: hasQuestionsError,
        errorMessage: questionsErrorMessage,
    } = useFetchCampaignQuestions(campaignId);

    // Ratings are stored on the ai_insights_insights row, so only projects on that storage
    // can be rated. LeadApp answers live in LeadDesk and are read-only for us.
    const canRate = storage === 'aiInsights';

    // Flag to determine whether to fetch insights
    const shouldFetchInsights = !hasQuestionsError && questions.length > 0;

    const {
        data: insightsData,
        isLoading: insightsLoading,
        isError: hasInsightsError,
        errorMessage: insightsErrorMessage,
        refetch: refetchInsights,
    } = useFetchCallInsights(callId, shouldFetchInsights, campaignId);

    const isLoading = questionsLoading || insightsLoading;

    // Merge questions with answers
    const questionsWithAnswers = useMemo(() => {
        const insightAnswers = insightsData?.answers ?? [];

        return questions.map((question) => {
            const matchingInsight = insightAnswers.find((answer: { id: string }) => answer.id === question.id);

            return {
                ...question,
                answer: matchingInsight ? matchingInsight.answer : null,
                prefilledFormula: matchingInsight?.prefilledFormula ?? null,
                questionModified: matchingInsight?.questionModified ?? false,
                insightQuestionId: matchingInsight?.questionId ?? null,
                feedback: matchingInsight?.feedback ?? null,
            };
        });
    }, [questions, insightsData]);

    const questionTypes = useMemo(() => {
        return Array.from(new Set(questionsWithAnswers.map((q) => q.type)));
    }, [questionsWithAnswers]);

    const filteredQuestions = useMemo(() => {
        if (selectedTypes.length === 0) {
            return questionsWithAnswers;
        }

        return questionsWithAnswers.filter((q) => selectedTypes.includes(q.type));
    }, [questionsWithAnswers, selectedTypes]);

    const visibleItems = useStaggeredAnimation({
        itemCount: filteredQuestions.length,
    });

    const toggleFilter = useCallback((type: QuestionType) => {
        setSelectedTypes((prev) => {
            if (prev.includes(type)) {
                return prev.filter((t) => t !== type);
            } else {
                return [...prev, type];
            }
        });
    }, []);

    if (isLoading) {
        return (
            <Paper sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <CircularProgress />
            </Paper>
        );
    }

    if (hasQuestionsError) {
        return (
            <Paper sx={{ p: 2 }}>
                <Alert severity="error" data-testid="questions-error-alert">
                    <Typography variant="body2">{questionsErrorMessage}</Typography>
                </Alert>
            </Paper>
        );
    }

    if (questions.length === 0) {
        return (
            <Box data-testid="no-questions-info-alert">
                <InsightsEmptyState
                    title="No insights yet for this call"
                    description={
                        <>
                            Add questions in the{' '}
                            <MuiLink component={RouterLink} to={`/projects/${campaignId}`} underline="hover">
                                project view
                            </MuiLink>{' '}
                            first to start generating insights.
                        </>
                    }
                />
            </Box>
        );
    }

    return (
        <Paper sx={{ p: 2, containerType: 'inline-size' }} data-testid="call-insights">
            <Stack spacing={1}>
                <CallInsightsHeader createdAt={insightsData?.createdAt ?? null} updatedAt={insightsData?.updatedAt ?? null} />
                {hasInsightsError && (
                    <Alert severity="error" data-testid="insights-error-alert">
                        <Typography variant="body2">{insightsErrorMessage}</Typography>
                    </Alert>
                )}
                <CallInsightsLayout
                    questionsContent={
                        <Stack spacing={1}>
                            {filteredQuestions.map((question, filterIndex) => {
                                const originalIndex = questionsWithAnswers.findIndex((q) => q.id === question.id);

                                return (
                                    <CallInsightItem
                                        key={question.id}
                                        question={question}
                                        allQuestions={questions}
                                        index={originalIndex}
                                        visible={visibleItems[filterIndex]}
                                        prefilledFormula={question.prefilledFormula}
                                        questionModified={question.questionModified ?? false}
                                        canRate={canRate}
                                        callId={callId}
                                        insightQuestionId={question.insightQuestionId}
                                        feedback={question.feedback}
                                    />
                                );
                            })}
                        </Stack>
                    }
                    filtersContent={
                        <CallInsightFilters questionTypes={questionTypes} selectedTypes={selectedTypes} onToggleFilter={toggleFilter} />
                    }
                />
            </Stack>
            {callId && <CallInsightsFetcher callId={callId} onSuccess={refetchInsights} />}
        </Paper>
    );
};

export default CallInsights;
